<!-- Using this opportunity to also grab the "default value" for naked mode -->

<template>
  <Dialog title="Switch to Naked mode?" @close="$emit('close')">
    <p>
      Naked mode allows you to fully customize the structure & style of your
      email. Learn more about it
      <a href="https://docs.buttondown.com/naked-mode" target="_blank">here</a>.
    </p>

    <div class="-mx-4 mt-4 border-y border-border">
      <Callout.Root variant="warning">
        <template #title>No safe way back.</template>

        Switching to another mode later will reset your email body completely
        clean.
      </Callout.Root>
    </div>

    <template #actions>
      <ActionButton
        text="Switch"
        variant="primary"
        :disabled="!defaultHtml"
        :pending="!defaultHtml"
        @click="handleConfirm"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed } from "vue";

import Callout from "@/design_system/Callout";
import Dialog from "@/design_system/shared/Dialog/Dialog.vue";
import ActionButton from "@/design_system/Utilities/ActionButton.vue";
import { useEmailDraftPreview } from "@/lib/api/queries/preview/email-draft";
import { useNewsletter } from "@/store/stores/account";
import type { NewsletterEmailTemplate } from "@/types/email";

const newsletter = useNewsletter();

const emit = defineEmits<{
  close: [];
  submit: [defaultBody: string];
}>();

const props = defineProps<{
  email: {
    id?: string;
    subject: string;
    body: string;
    template?: NewsletterEmailTemplate | null;
  };
}>();

const previewSource = useEmailDraftPreview(
  () => {
    const email = props.email;
    // Seed from the email's design, falling back to the newsletter's when
    // unset; a naked email has no themed output to seed from, so use the
    // newsletter's design there too.
    const newsletterTemplate =
      (newsletter.value.template as NewsletterEmailTemplate | undefined) ??
      "modern";
    let template: NewsletterEmailTemplate =
      email.template ?? newsletterTemplate;
    if (template === "naked") {
      template = newsletterTemplate;
    }
    return {
      id: email.id,
      subject: email.subject,
      body: email.body,
      template,
    };
  },
  {
    context: "email",
  }
);

const defaultHtml = computed(() => {
  const html = previewSource.html.value;
  if (html === undefined) {
    return;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const unsub_link = doc.querySelector(".email-actions--unsubscribe a");
  const subject_link = doc.querySelector(".subject a");

  if (unsub_link) {
    // Replace the literal unsubscribe URL (rendered for a specific
    // subscriber) with a template tag, since the result is being copied
    // into the email body and needs to re-resolve per recipient.
    unsub_link.setAttribute("href", "{{ unsubscribe_url }}");
  }

  if (subject_link) {
    subject_link.setAttribute("href", "{{ email.absolute_url }}");
  }

  return doc.documentElement.outerHTML;
});

const handleConfirm = () => {
  if (!defaultHtml.value) {
    return;
  }

  emit("close");
  emit("submit", defaultHtml.value);
};
</script>
